import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DateInputFillMode, DateInputRounded } from '@progress/kendo-angular-dateinputs';
import { EditorResizableOptions } from '@progress/kendo-angular-editor';
import { CheckBoxRounded, InputSize } from '@progress/kendo-angular-inputs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { CookieAPIService } from 'src/app/extensions/services/api/cookie-api.service';

@Component({
  selector: 'terms-and-conditions-detail',
  templateUrl: './terms-and-conditions-detail.component.html',
  styleUrls: ['./terms-and-conditions-detail.component.scss'],
  standalone: false,
})
export class TermsAndConditionsDetailComponent implements OnInit {
  @ViewChild('form') public form: NgForm;

  public size: InputSize = 'large';
  public rounded: CheckBoxRounded = 'large';
  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public firstAvailableStartDateForNewCookieMessage: Date;
  public props: app.models.DynamicPropsDictionary;
  public startDate: Date = null;
  public initialStartDate: Date = null;
  public isEditorFocused = false;
  public resizable: boolean | EditorResizableOptions = {
    minHeight: 300,
    maxHeight: 800,
  };
  public cookie: app.models.CookieModel = {
    message: '',
    required: false,
    startDate: null,
    endDate: null,
    title: '',
    active: true,
    earliestStartDateAvailable: null,
  } as app.models.CookieModel;

  loading: boolean = false;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _cookieAPIService: CookieAPIService,
    private _router: Router,
    private _notificationService: NotificationService,
    private _translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.cookie.cookieMessageId = this._activatedRoute.snapshot.params['cookieId'];
    if (this.cookie.cookieMessageId) {
      this._cookieAPIService.getCookieMessageById(this.cookie.cookieMessageId).subscribe(result => {
        this.cookie = result.data;
        this.props = result.props;
        this.startDate = this.initialStartDate = new Date(this.cookie.startDate);
        this.startDate.setHours(0, 0, 0, 0);
      });
    } else {
      this._cookieAPIService.getFirstAvailableStartDateForNewCookieMessage().subscribe(result => {
        this.firstAvailableStartDateForNewCookieMessage = result;
      });
    }
  }

  redirectTermsAndConditions() {
    this._router.navigate(['terms-and-conditions']);
  }

  disabledDates = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today) {
      if (this.cookie?.cookieMessageId != null) {
        const earliestStartDate = new Date(this.cookie.earliestStartDateAvailable);
        earliestStartDate.setHours(0, 0, 0, 0);
        if (this.cookie.endDate != null) {
          const endDate = new Date(this.cookie.endDate);
          endDate.setHours(0, 0, 0, 0);
          return date < earliestStartDate || date > endDate;
        }
        return date < earliestStartDate;
      }
      const firstAvailableStartDateForNewCookie = new Date(this.firstAvailableStartDateForNewCookieMessage);
      firstAvailableStartDateForNewCookie.setHours(0, 0, 0, 0);
      return date < firstAvailableStartDateForNewCookie;
    }
    return true;
  };

  changeStartDateDatePicker() {
    this.cookie.startDate = this.startDate;
  }

  saveCookieHandler() {
    if (!this.form.valid) {
      return;
    }
    this.loading = true;
    this._cookieAPIService.saveCookieMessage(this.cookie).subscribe(response => {
      if (response.isSuccess) {
        this._notificationService.showSuccess(
          this._translateService.instant('termsAndConditions.add.notifications.cookieMessageSaved'),
          this._translateService.instant('common.success')
        );
        this.form.resetForm();
        this.redirectTermsAndConditions();
      } else {
        this._notificationService.showError(
          this._translateService.instant('termsAndConditions.add.notifications.cookieMessageSaveFailed'),
          this._translateService.instant('common.error')
        );
      }

      this.loading = false;
    });
  }

  isCookieEditable() {
    return this.initialStartDate <= new Date() && this.cookie.cookieMessageId != null;
  }
}
